import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { leadsApi } from "@/api/leads";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { FilterMultiSelect } from "@/components/ui/filter-multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { hospitalAdminApi } from "@/api/hospitalAdmin";
import { reportHospitalStaffParams } from "@/utils/reportHospitalStaffParams";
import type { Staff } from "@/types/types";
import {
  EXITED_EMPLOYEES_BUCKET_ID,
  EXITED_EMPLOYEES_BUCKET_LABEL,
} from "@/constants/crmExitedEmployees";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { formatLeadStatusLabel, formatLeadSubStatusLabel } from "@/lib/crmLeadStyles";
import type { LeadStatus, LeadSubStatus } from "@/api/leads";
import { useAuth } from "@/contexts/AuthContext";
import { isCrmViewOnlyUser, shouldMaskLeadPhone } from "@/utils/crmAccess";
import { maskPhoneDisplay } from "@/utils/stringUtils";

type Props = {
  from: string;
  to: string;
  hospitalId?: string;
};

const OPEN_STATUSES: LeadStatus[] = ["NEW", "CONTACTED", "INTERESTED", "FOLLOW_UP"];

const AGE_BUCKETS: Array<{ key: string; label: string }> = [
  { key: "61_plus", label: "61+ days" },
  { key: "31_60", label: "31–60 days" },
  { key: "15_30", label: "15–30 days" },
  { key: "8_14", label: "8–14 days" },
  { key: "4_7", label: "4–7 days" },
  { key: "0_3", label: "0–3 days" },
];

const DEFAULT_SOURCES = [
  "GOOGLE_PAID",
  "GMB",
  "META",
  "META_PAID",
  "WHATSAPP_INSTA_ADS",
  "WHATSAPP",
  "PRACTO",
  "HEXA",
  "REFERRAL",
  "WALK_IN",
  "OFFLINE",
  "OTHER",
  "EXOTEL",
  "ORGANIC_DIRECT",
  "ORGANIC_GOOGLE",
  "DIRECT_LANDING",
  "INSTAGRAM_ORGANIC",
];

const STATUS_COLORS: Record<LeadStatus, string> = {
  NEW: "#0284c7",
  CONTACTED: "#d97706",
  INTERESTED: "#0891b2",
  FOLLOW_UP: "#7c3aed",
  CONVERTED: "#059669",
  LOST: "#64748b",
  CLOSED: "#4d7c0f",
};

function fmtDays(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return `${Math.round(n)} d`;
}

function cellHeat(count: number, max: number): string {
  if (count <= 0 || max <= 0) return "bg-white";
  const t = count / max;
  if (t >= 0.75) return "bg-rose-200 text-rose-950";
  if (t >= 0.5) return "bg-amber-200 text-amber-950";
  if (t >= 0.25) return "bg-sky-100 text-sky-950";
  return "bg-slate-50 text-slate-800";
}

export function LeadOpenAgeingTab({ from, to, hospitalId }: Props) {
  const { user } = useAuth();
  const crmViewOnly = isCrmViewOnlyUser(user);
  const [downloading, setDownloading] = useState(false);
  const [sourceFilters, setSourceFilters] = useState<string[]>([]);
  const [assignedToId, setAssignedToId] = useState<string>("all");

  const { data: filterOptions } = useQuery({
    queryKey: ["lead-filter-options", hospitalId],
    queryFn: () => leadsApi.getLeadFilterOptions(hospitalId ? { hospitalId } : undefined),
  });

  const { data: staffRows = [] } = useQuery<Staff[]>({
    queryKey: ["lead-open-ageing-staff", hospitalId],
    queryFn: () =>
      hospitalAdminApi.getStaff(reportHospitalStaffParams(hospitalId, { includeInactive: true })),
  });

  const sources = useMemo(() => {
    const fromApi = filterOptions?.sources ?? [];
    const merged = new Map<string, string>();
    for (const s of [...fromApi, ...DEFAULT_SOURCES]) {
      const t = String(s).trim();
      if (t) merged.set(t.toUpperCase(), t);
    }
    return [...merged.values()].sort((a, b) => a.localeCompare(b));
  }, [filterOptions?.sources]);

  const sourceFilterOptions = useMemo(
    () => sources.map((s) => ({ value: s, label: s })),
    [sources]
  );

  const ownerOptions = useMemo(
    () =>
      staffRows
        .filter((staff) => {
          const role = String(staff.role || "").toUpperCase();
          if (role === "SALES_MANAGER") return staff.status === "ACTIVE";
          if (role === "SALES_PERSON") return staff.status === "ACTIVE" && !staff.exitedAt;
          return false;
        })
        .map((staff) => ({
          id: staff.id,
          name: staff.name,
          role: String(staff.role || "").toUpperCase(),
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [staffRows]
  );

  const reportParams = useMemo(() => {
    const p: Record<string, string> = { from, to };
    if (hospitalId) p.hospitalId = hospitalId;
    if (sourceFilters.length) p.source = sourceFilters.join(",");
    if (assignedToId !== "all") p.assignedToId = assignedToId;
    return p;
  }, [from, to, hospitalId, sourceFilters, assignedToId]);

  const query = useQuery({
    queryKey: ["lead-open-ageing", reportParams, user?.role],
    queryFn: async () => {
      const res = await leadsApi.getOpenAgeingReport(reportParams);
      return res.data.data;
    },
  });

  const matrixLookup = useMemo(() => {
    const map = new Map<string, number>();
    for (const cell of query.data?.matrix ?? []) {
      map.set(`${cell.status}:${cell.ageBucket}`, cell.count);
    }
    return map;
  }, [query.data?.matrix]);

  const maxCell = useMemo(() => {
    let max = 0;
    for (const v of matrixLookup.values()) {
      if (v > max) max = v;
    }
    return max;
  }, [matrixLookup]);

  const chartRows = useMemo(() => {
    return AGE_BUCKETS.map((bucket) => {
      const row: Record<string, string | number> = { bucket: bucket.label };
      for (const st of OPEN_STATUSES) {
        row[st] = matrixLookup.get(`${st}:${bucket.key}`) ?? 0;
      }
      row.total = OPEN_STATUSES.reduce(
        (sum, st) => sum + Number(row[st] ?? 0),
        0
      );
      return row;
    });
  }, [matrixLookup]);

  const downloadCsv = async () => {
    try {
      setDownloading(true);
      const res = await leadsApi.downloadOpenAgeingReport(reportParams);
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `open-lead-ageing-${query.data?.asOf ?? to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download CSV");
    } finally {
      setDownloading(false);
    }
  };

  const leads = (query.data?.leads ?? []) as Array<{
    id: string;
    patientName: string;
    phone: string;
    source: string;
    status: LeadStatus;
    leadSubStatus: LeadSubStatus | null;
    ageDays: number;
    ageBucket: string;
    createdAt: string;
    assignedToName: string | null;
    nextFollowUpAt: string | null;
  }>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Snapshot of open pipeline leads (New, Contacted, Interested, Follow-up). Age is days since
        lead creation in IST, calculated as of the <strong>To</strong> date above.
      </p>

      <div className="flex flex-wrap items-end gap-3 p-3 border rounded-md bg-muted/30">
        <div className="space-y-1">
          <Label className="text-xs">Sales person</Label>
          <Select value={assignedToId} onValueChange={setAssignedToId}>
            <SelectTrigger className="w-[14rem] h-9">
              <SelectValue placeholder="All owners" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All owners</SelectItem>
              {ownerOptions.map((owner) => (
                <SelectItem key={owner.id} value={owner.id}>
                  {owner.name}
                  {owner.role === "SALES_MANAGER" ? " (Manager)" : ""}
                </SelectItem>
              ))}
              <SelectItem value={EXITED_EMPLOYEES_BUCKET_ID}>
                {EXITED_EMPLOYEES_BUCKET_LABEL}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Lead source</Label>
          <FilterMultiSelect
            values={sourceFilters}
            onValuesChange={setSourceFilters}
            options={sourceFilterOptions}
            placeholder="Source"
            allLabel="All sources"
            triggerClassName="w-[12rem]"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Open leads</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold tabular-nums">{query.data?.totalOpen ?? "—"}</p>
            <p className="text-xs text-muted-foreground mt-1">As of {query.data?.asOf ?? to}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Median age</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold tabular-nums">
              {fmtDays(query.data?.medianAgeDays)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Mean age</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold tabular-nums">
              {fmtDays(query.data?.meanAgeDays)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">61+ days</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold tabular-nums text-rose-700">
              {query.data?.byAgeBucket?.["61_plus"] ?? 0}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Stale open leads</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <div>
            <CardTitle>Age × status matrix</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Count of open leads in each age bucket and current status.
            </p>
          </div>
          {!crmViewOnly && (
            <Button variant="outline" size="sm" disabled={downloading} onClick={downloadCsv}>
              <Download className="h-4 w-4 mr-1" />
              {downloading ? "…" : "CSV"}
            </Button>
          )}
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {query.isLoading ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
          ) : (
            <table className="min-w-full text-sm border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="py-2 pr-3 text-left font-medium text-muted-foreground">Age</th>
                  {OPEN_STATUSES.map((st) => (
                    <th key={st} className="py-2 px-2 text-center font-medium text-muted-foreground">
                      {formatLeadStatusLabel(st)}
                    </th>
                  ))}
                  <th className="py-2 pl-2 text-right font-medium text-muted-foreground">Row total</th>
                </tr>
              </thead>
              <tbody>
                {AGE_BUCKETS.map((bucket) => {
                  const rowTotal = OPEN_STATUSES.reduce(
                    (sum, st) => sum + (matrixLookup.get(`${st}:${bucket.key}`) ?? 0),
                    0
                  );
                  return (
                    <tr key={bucket.key} className="border-b last:border-b-0">
                      <td className="py-2 pr-3 font-medium whitespace-nowrap">{bucket.label}</td>
                      {OPEN_STATUSES.map((st) => {
                        const count = matrixLookup.get(`${st}:${bucket.key}`) ?? 0;
                        return (
                          <td key={st} className="p-1">
                            <div
                              className={`rounded-md py-2 text-center tabular-nums font-semibold ${cellHeat(count, maxCell)}`}
                            >
                              {count || "—"}
                            </div>
                          </td>
                        );
                      })}
                      <td className="py-2 pl-2 text-right font-semibold tabular-nums">{rowTotal}</td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 bg-slate-50 font-semibold">
                  <td className="py-2 pr-3">Total</td>
                  {OPEN_STATUSES.map((st) => (
                    <td key={st} className="py-2 px-2 text-center tabular-nums">
                      {query.data?.byStatus?.[st] ?? 0}
                    </td>
                  ))}
                  <td className="py-2 pl-2 text-right tabular-nums">{query.data?.totalOpen ?? 0}</td>
                </tr>
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Age distribution by status</CardTitle>
        </CardHeader>
        <CardContent className="h-80">
          {query.isLoading ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Loading chart…</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                {OPEN_STATUSES.map((st) => (
                  <Bar
                    key={st}
                    dataKey={st}
                    name={formatLeadStatusLabel(st)}
                    stackId="age"
                    fill={STATUS_COLORS[st]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Open leads detail</CardTitle>
          <p className="text-sm text-muted-foreground">Sorted by age (oldest first).</p>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="py-2 text-left font-medium">Patient</th>
                <th className="py-2 text-left font-medium">Phone</th>
                <th className="py-2 text-left font-medium">Source</th>
                <th className="py-2 text-left font-medium">Status</th>
                <th className="py-2 text-left font-medium">Sub-status</th>
                <th className="py-2 text-right font-medium">Age</th>
                <th className="py-2 text-left font-medium">Owner</th>
                <th className="py-2 text-left font-medium">Next F/U</th>
              </tr>
            </thead>
            <tbody>
              {query.isLoading ? (
                <tr>
                  <td colSpan={8} className="py-6 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : leads.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-6 text-center text-muted-foreground">
                    No open leads in scope.
                  </td>
                </tr>
              ) : (
                leads.slice(0, 100).map((row) => (
                  <tr key={row.id} className="border-b last:border-b-0">
                    <td className="py-2 pr-2 font-medium whitespace-nowrap">{row.patientName}</td>
                    <td className="py-2 pr-2 whitespace-nowrap">
                      {shouldMaskLeadPhone(user) ? maskPhoneDisplay(row.phone) : row.phone}
                    </td>
                    <td className="py-2 pr-2">{row.source}</td>
                    <td className="py-2 pr-2">{formatLeadStatusLabel(row.status)}</td>
                    <td className="py-2 pr-2 text-muted-foreground">
                      {row.leadSubStatus
                        ? formatLeadSubStatusLabel(row.leadSubStatus)
                        : "—"}
                    </td>
                    <td
                      className={`py-2 pr-2 text-right tabular-nums font-semibold ${
                        row.ageDays >= 61 ? "text-rose-700" : row.ageDays >= 31 ? "text-amber-700" : ""
                      }`}
                    >
                      {row.ageDays} d
                    </td>
                    <td className="py-2 pr-2">{row.assignedToName ?? "—"}</td>
                    <td className="py-2 whitespace-nowrap text-muted-foreground">
                      {row.nextFollowUpAt
                        ? new Date(row.nextFollowUpAt).toLocaleDateString("en-IN")
                        : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {leads.length > 100 && (
            <p className="text-xs text-muted-foreground mt-2">
              Showing 100 of {leads.length}. Download CSV for the full list.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
